import { MobileNavFrame } from './components/mobile-nav-frame'
import { MobileAgentComposer } from './components/chat/mobile-agent-composer'
import { MobileAgentDrawer } from './components/chat/mobile-agent-drawer'
import { initialMobileAgentSurface, usesMobileAgentDrawer, usesMobileAgentSwipe } from './lib/mobile-agent-presentation'
import { useState, useRef, useCallback, useEffect, useMemo, type CSSProperties } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate, useSearchParams, useNavigate, useLocation } from 'react-router-dom'
import { ThemeProvider } from './components/theme-provider'
import { APP_THEME_STORAGE_KEY } from './lib/app-theme'
import { Form, MessageSquare, MessageSquarePlus, PanelRightOpen, Sparkles, SquarePen } from 'lucide-react'
import { AnimatedTitle } from './components/animated-title'
import { ChatPane, type ChatPaneHandle } from './components/chat/chat-pane'
import { SidebarProvider, SidebarInset } from './components/ui/sidebar'
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from './components/ui/resizable'
import { Button } from './components/ui/button'
import type { GroupImperativeHandle } from 'react-resizable-panels'
import { animate } from 'framer-motion'
import { AppSidebar } from './components/app-sidebar'
import { Profile } from './components/profile'
import { TopSkillDetailPage } from './components/profile/top-skill-detail-page'
import { PublicLandingPage } from './components/landing/public-landing-page'
import { LoginPage } from './components/login-page'
import { SignupPage } from './components/auth/signup-page'
import { CheckEmailPage } from './components/auth/check-email-page'
import { SignupPendingPage } from './components/auth/signup-pending-page'
import { AuthPageShell } from './components/auth/auth-page-shell'
import { PasswordResetRequest } from './components/auth/password-reset-request'
import { PasswordResetConfirm } from './components/auth/password-reset-confirm'
import { PasswordHintBanner } from './components/auth/password-hint-banner'
import { EmailVerify } from './components/auth/email-verify'
import { InviteAccept } from './components/auth/invite-accept'
import { AuthProviderCallback } from './components/auth/auth-provider-callback'
import { AuthProviderSignup } from './components/auth/auth-provider-signup'
import { PublicShareRoutes } from './components/public/public-share-routes'
import { isPublicSharePath } from './constants/public-share-routes'
import { LegalRoutes } from './components/legal/legal-routes'
import { isLegalPath } from './constants/legal-routes'
import { isYeshumanConceptsPath } from './constants/yeshuman-marketing-routes'
import { YeshumanConceptsRoutes } from './components/landing/yeshuman/yeshuman-concepts-routes'
import { OnboardingWizard } from './components/onboarding/onboarding-wizard'
import { PlatformRoutes } from './components/routes/platform-routes'
import { DeveloperRoutes } from './components/routes/developer-routes'
import { AgencyRoutes } from './components/routes/agency-routes'
import { MemoriesPage } from './components/memories/memories-page'
import { ThreadsPage } from './components/threads/threads-page'
import { ThreadDetailPage } from './components/threads/thread-detail-page'
import { UserFilesPage } from './components/files/user-files-page'
import { useSSE } from './hooks/use-sse'
import { useAuth } from './hooks/use-auth'
import { useIsMobile } from './hooks/use-mobile'
import { useUiVisualViewport, isIOSSafari } from './hooks/use-ui-visual-viewport'
import { UiViewportDebug } from './components/dev/ui-viewport-debug'
import { useThreadController } from './hooks/use-thread-controller'
import { useSurfaceNavigation } from './hooks/use-surface-navigation'
import { useDocumentHead } from './hooks/use-document-head'
import { useQueryClient } from '@tanstack/react-query'
import { getFocusRoute } from './utils/focus-routes'
import { NavigateWithThread } from './components/navigate-with-thread'
import { FocusRouteGate } from './components/focus-route-gate'
import { resolveComposerConfig } from './lib/resolve-composer-config'
import { resolveComposerStarters } from './lib/resolve-composer-starters'
import {
  filterComposerMenuCategories,
  resolveStaticComposerMenuCategories,
} from './lib/resolve-composer-menu'
import type { ComposerCapabilityExample } from './types/composer'
import { resolveOnboardingComposer } from './lib/resolve-onboarding-composer'
import { useOnboarding } from './hooks/use-onboarding'
import { AGENT_UI_ENABLED, CURRENT_CLIENT, isDocumentsEnabled, isProductFeedbackEnabled, isProductFeedbackHeaderIconEnabled } from './constants'
import { resolveGuestIndex, type ClientWithLanding } from './landing/resolve-landing'
import { getPluginSlots } from './plugin/slots'
import { PluginPageCanvasProvider } from './plugin/canvases'
import { pluginCanvasPageRoutes } from './plugin/canvas-page-routes'
import { DocumentsPage } from './components/documents/documents-page'
import { DocumentPage } from './components/documents/document-page'
import { DocumentNewPage } from './components/documents/document-new-page'
import { SettingsRoutes } from './components/settings/settings-routes'
import { isSettingsPath } from './constants/settings-nav'
import { isEmployerOrganisationSettingsPath } from './constants/employer-organisation-settings-nav'
import { EmployerOrganisationProvider } from '@/context/employer-organisation-context'
import { AgentListeningProvider } from '@/context/agent-listening-context'
import { AgentPanelProvider } from '@/context/agent-panel-context'
import { UI_AGENT_MAGIC_LINK_KICKOFF_KEY } from '@/constants/agent-kickoff'
import type { ChatMessage } from './types'
import { collapseObjectUpdateMessagesByRunId } from './utils/object-update-utils'
import { navigateWithThreadPreservation } from './utils/thread-navigation'
import { shouldResetChatOnUserChange } from './lib/agent-chat-session-reset'
import { AGENT_HIGHLIGHT_PARAM, useAgentHighlightFromLocation } from './agent-highlight'
import {
  isJobsDetailPathname,
  isJobsListPathname,
  listReturnPathFromLocation,
  readRestoreScrollTop,
  takeRememberedListScroll,
} from './lib/list-pagination'
import { ClientBackground } from './components/backgrounds/client-background'
import { MobileSidebarMenuButton } from './components/mobile-sidebar-menu-button'
import { NotificationBell } from './components/notifications/notification-bell'
import { FeedbackHeaderButton } from './components/feedback/feedback-header-button'
import { ProductInviteHeaderButton } from './components/invite/product-invite-header-button'
import { NotificationsPage } from './components/notifications/notifications-page'
import { FeedbackPage } from './components/feedback/feedback-page'
import { ArtifactCanvasProvider } from './components/artifact/artifact-canvas-context'
import { PagePaneLayoutProvider, PagePaneScrollRegion } from '@/context/page-pane-layout-context'
import { AppShellSkeleton } from './components/layout/app-shell-skeleton'
import { DashboardPageSkeleton } from './components/dashboard/dashboard-page-skeleton'
import { cn } from './lib/utils'
import { toast } from 'sonner'
import { authorizedFetch } from './lib/api'
import { resolveServerMessageId } from './lib/chat-suggestions-cursor'
import type { ClientTheme } from '@/types/theme'
import './App.css'

interface UserFocus {
  current_focus: string | null
  available_foci: string[]
  focus_confirmed: boolean
}

/** `/` -> focus-route SPA redirects should not steal mobile chat (composer-first). Pathname effect skips pane switch for these only. */
function isMobileFocusBootstrap(prevPath: string, nextPath: string): boolean {
  if (prevPath !== '/') return false;
  return /^\/(candidate|employer|recruiter|interviewer|platform|developer|calls|patient|practitioner|onboarding)(\/|$)/.test(
    nextPath
  );
}

function AppContent() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  useAgentHighlightFromLocation();

  const isSettingsRoute =
    isSettingsPath(location.pathname) || isEmployerOrganisationSettingsPath(location.pathname);
  const agentAvailableForRoute = AGENT_UI_ENABLED && !isSettingsRoute;

  // Update document title and favicon based on client config
  useDocumentHead();

  // Chat/page split layout (persisted to localStorage)
  const [chatPageSizes, setChatPageSizes] = useState<number[]>(() => {
    if (typeof window === 'undefined') return [43.75, 56.25];
    try {
      const saved = localStorage.getItem('chat-page-split');
      if (saved) {
        const parsed = JSON.parse(saved) as number[];
        if (Array.isArray(parsed) && parsed.length === 2) return parsed;
      }
    } catch (e) { /* ignore */ }
    return [43.75, 56.25];
  });

  const groupRef = useRef<GroupImperativeHandle>(null);
  const expandedSizesRef = useRef<number[]>([43.75, 56.25]);
  const [isAnimating, setIsAnimating] = useState(false);
  const isMobile = useIsMobile();
  const mobileDrawerEnabled = usesMobileAgentDrawer(CURRENT_CLIENT.layout);
  const mobileSwipeEnabled = usesMobileAgentSwipe(CURRENT_CLIENT.layout);
  const mobileAgentSwipe = isMobile && mobileSwipeEnabled;
  const mobileAgentDrawer = isMobile && mobileDrawerEnabled;
  const [drawerKeyboardOpen, setDrawerKeyboardOpen] = useState(false);
  const [activeMobilePane, setActiveMobilePane] = useState<'chat' | 'page'>(() =>
    initialMobileAgentSurface(AGENT_UI_ENABLED, mobileDrawerEnabled || mobileSwipeEnabled, searchParams.has('thread'))
  );
  const [isMobileHeaderVisible, setIsMobileHeaderVisible] = useState(true);
  const mobilePaneTrackRef = useRef<HTMLDivElement>(null);
  const mobilePaneDragDeltaRef = useRef(0);
  const mobilePaneTouchStartRef = useRef<{ x: number; y: number } | null>(null);
  const mobilePaneGestureRef = useRef<'pending' | 'horizontal' | 'vertical'>('pending');
  const pageScrollRef = useRef<HTMLDivElement>(null);
  /** Mirrors last applied mobile header visibility so scroll logic can debounce toggles without stale React state. */
  const appliedChromeVisibleRef = useRef(true);
  const lastChromeToggleAtRef = useRef(0);
  const mobileScrollRafRef = useRef(0);
  const pagePaneRef = useRef<HTMLDivElement | null>(null);
  /** Radix portals must receive a real element; reading pagePaneRef.current during render stays null until ref attaches (no re-render). */
  const [pagePanePortalHost, setPagePanePortalHost] = useState<HTMLElement | null>(null);
  const setPagePaneEl = useCallback((node: HTMLDivElement | null) => {
    pagePaneRef.current = node;
    setPagePanePortalHost(node);
  }, []);
  const previousPathnameRef = useRef<string | null>(null);
  const listScrollPrevPathRef = useRef<string | null>(null);
  const previousFocusPathRef = useRef<string | null>(null);
  const publicShareScrollRef = useRef<HTMLDivElement>(null);
  const suppressNextRouteSurfaceIntentRef = useRef(false);

  // Agent collapse state (persisted to localStorage) - must be declared before handleChatPageLayoutChange
  const [agentCollapsed, setAgentCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    try {
      const agentSaved = localStorage.getItem('agent-collapsed') === 'true';
      const pageSaved = localStorage.getItem('page-collapsed') === 'true';
      if (agentSaved && pageSaved) return false;
      return agentSaved;
    } catch (_e) { return false; }
  });
  const [pageCollapsed, setPageCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    try {
      const agentSaved = localStorage.getItem('agent-collapsed') === 'true';
      const pageSaved = localStorage.getItem('page-collapsed') === 'true';
      if (agentSaved && pageSaved) return true;
      return pageSaved;
    } catch (_e) { return false; }
  });

  const handleChatPageLayoutChanged = useCallback((layout: { [id: string]: number }) => {
    const chat = layout.chat ?? 0;
    const page = layout.page ?? 100;
    if (pageCollapsed) {
      if (page > 0) {
        groupRef.current?.setLayout({ chat: 100, page: 0 });
      }
      return;
    }
    if (agentCollapsed) {
      if (chat > 0) {
        groupRef.current?.setLayout({ chat: 0, page: 100 });
      }
      return;
    }
    if (!isAnimating && chat > 0) {
      expandedSizesRef.current = [chat, page];
    }
    setChatPageSizes([chat, page]);
    try {
      localStorage.setItem('chat-page-split', JSON.stringify([chat, page]));
    } catch (_e) { /* ignore */ }
  }, [agentCollapsed, pageCollapsed, isAnimating]);

  const handleAgentCollapsedToggle = useCallback(async () => {
    if (isAnimating) return;
    const [a, b] = expandedSizesRef.current;

    if (agentCollapsed) {
      setPageCollapsed(false);
      setAgentCollapsed(false);
      setIsAnimating(true);
      requestAnimationFrame(() => {
        animate(0, 1, {
          duration: 0.2,
          ease: 'easeOut',
          onUpdate: (t) => {
            groupRef.current?.setLayout({ chat: a * t, page: 100 - (100 - b) * t });
          },
        }).then(() => {
          setIsAnimating(false);
          try {
            localStorage.setItem('agent-collapsed', 'false');
            localStorage.setItem('page-collapsed', 'false');
          } catch (_e) { /* ignore */ }
        });
      });
    } else {
      expandedSizesRef.current = [...chatPageSizes];
      setPageCollapsed(false);
      setIsAnimating(true);
      requestAnimationFrame(() => {
        animate(0, 1, {
          duration: 0.2,
          ease: 'easeOut',
          onUpdate: (t) => {
            groupRef.current?.setLayout({ chat: a * (1 - t), page: b + (100 - b) * t });
          },
        }).then(() => {
          setAgentCollapsed(true);
          setIsAnimating(false);
          try {
            localStorage.setItem('agent-collapsed', 'true');
            localStorage.setItem('page-collapsed', 'false');
          } catch (_e) { /* ignore */ }
        });
      });
    }
  }, [agentCollapsed, chatPageSizes, isAnimating]);

  const handlePageCollapsedToggle = useCallback(async () => {
    if (isAnimating) return;
    const [a, b] = expandedSizesRef.current;

    if (pageCollapsed) {
      setPageCollapsed(false);
      setIsAnimating(true);
      requestAnimationFrame(() => {
        animate(0, 1, {
          duration: 0.2,
          ease: 'easeOut',
          onUpdate: (t) => {
            groupRef.current?.setLayout({
              chat: 100 - (100 - a) * t,
              page: b * t,
            });
          },
        }).then(() => {
          setIsAnimating(false);
          try {
            localStorage.setItem('page-collapsed', 'false');
          } catch (_e) { /* ignore */ }
        });
      });
    } else {
      if (!agentCollapsed) {
        expandedSizesRef.current = [...chatPageSizes];
      }
      const startChat = agentCollapsed ? 0 : chatPageSizes[0];
      const startPage = agentCollapsed ? 100 : chatPageSizes[1];
      setAgentCollapsed(false);
      setIsAnimating(true);
      requestAnimationFrame(() => {
        animate(0, 1, {
          duration: 0.2,
          ease: 'easeOut',
          onUpdate: (t) => {
            groupRef.current?.setLayout({
              chat: startChat + (100 - startChat) * t,
              page: startPage * (1 - t),
            });
          },
        }).then(() => {
          setPageCollapsed(true);
          setIsAnimating(false);
          try {
            localStorage.setItem('page-collapsed', 'true');
            localStorage.setItem('agent-collapsed', 'false');
          } catch (_e) { /* ignore */ }
        });
      });
    }
  }, [agentCollapsed, pageCollapsed, chatPageSizes, isAnimating]);


  // Get auth state
  const { isAuthenticated, token, isInitialized, logout, user } = useAuth();
  // Keyboard inset + scroll lock: any mobile agent UI (not only after auth). Gating on auth made
  // Safari/CSS vars easy to miss during testing and did not match when layout runs.
  const uiVvTrackingEnabled = isMobile && AGENT_UI_ENABLED && !mobileDrawerEnabled;
  const { isCompact: isMobileViewportCompact } = useUiVisualViewport(uiVvTrackingEnabled);
  // Get query client for invalidation
  const queryClient = useQueryClient();

  // Composer input state lives inside ChatPane; App keeps an imperative handle
  // for magic-link kickoff and starter picks.
  const chatPaneRef = useRef<ChatPaneHandle>(null);
  const chatDraftRef = useRef('');
  // Focus state
  const [userFocus, setUserFocus] = useState<UserFocus | null>(null);
  const { status: onboardingStatus } = useOnboarding();

  // Animation trigger
  const animationTriggerRef = useRef<(() => void) | null>(null);

  const isLoadingRef = useRef(false);
  const loadSuggestionsForThreadRef = useRef<
    ((threadId: string, latestAssistant: {
      serverMessageId: string;
      runId?: string | null;
      anchorMessageId?: string;
    }) => Promise<boolean>) | null
  >(null);
  const clearChatSuggestionsRef = useRef<(() => void) | null>(null);
  const authFailureHandlerRef = useRef<() => void>(() => {});
  const prevUserIdRef = useRef<number | null>(null);

  // Sync expandedSizesRef with chatPageSizes when split is visible and not animating
  useEffect(() => {
    if (!isAnimating && !agentCollapsed && !pageCollapsed) {
      expandedSizesRef.current = [...chatPageSizes];
    }
  }, [chatPageSizes, agentCollapsed, pageCollapsed, isAnimating]);

  // Mobile: header hides on scroll down; it only reappears once scrollTop is within a tight px band of the top (no “reveal on scroll up” mid-page).
  useEffect(() => {
    if (mobileAgentSwipe || !agentAvailableForRoute || !isMobile || isMobileViewportCompact || activeMobilePane === 'chat') {
      appliedChromeVisibleRef.current = true;
      setIsMobileHeaderVisible(true);
      return;
    }
    const scrollEl = pageScrollRef.current;
    if (!scrollEl) return;

    let lastScrollTop = scrollEl.scrollTop;
    /** Header chrome shows only inside this band — deliberate “almost at top” before chrome returns. */
    const SHOW_HEADER_TOP_PX = 24;
    /** Ignore scroll deltas smaller than this so rubber-band / sub-pixel jitter doesn’t toggle hide/show. */
    const SCROLL_EPS = 6;
    /** Minimum time between hide/show toggles (except inside SHOW_HEADER_TOP_PX), coerced with rAF to reduce bounce. */
    const MIN_TOGGLE_MS = 280;

    const applyChromeFromScroll = () => {
      const st = scrollEl.scrollTop;
      if (st === lastScrollTop) return;

      let nextVisible: boolean;
      if (st <= SHOW_HEADER_TOP_PX) {
        nextVisible = true;
      } else {
        const delta = st - lastScrollTop;
        if (delta > SCROLL_EPS) {
          nextVisible = false;
        } else if (delta < 0) {
          lastScrollTop = st;
          return;
        } else {
          return;
        }
      }

      if (nextVisible !== appliedChromeVisibleRef.current) {
        const now = Date.now();
        if (st > SHOW_HEADER_TOP_PX && now - lastChromeToggleAtRef.current < MIN_TOGGLE_MS) {
          lastScrollTop = st;
          return;
        }
        lastChromeToggleAtRef.current = now;
      }

      lastScrollTop = st;
      appliedChromeVisibleRef.current = nextVisible;
      setIsMobileHeaderVisible(nextVisible);
    };

    const handleScroll = () => {
      if (mobileScrollRafRef.current !== 0) return;
      mobileScrollRafRef.current = requestAnimationFrame(() => {
        mobileScrollRafRef.current = 0;
        applyChromeFromScroll();
      });
    };

    scrollEl.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      scrollEl.removeEventListener('scroll', handleScroll);
      if (mobileScrollRafRef.current !== 0) {
        cancelAnimationFrame(mobileScrollRafRef.current);
        mobileScrollRafRef.current = 0;
      }
    };
  }, [isMobile, activeMobilePane, isMobileViewportCompact, agentAvailableForRoute, mobileAgentSwipe]);

  useEffect(() => {
    if (!isMobile) {
      mobilePaneDragDeltaRef.current = 0;
      mobilePaneTouchStartRef.current = null;
      mobilePaneGestureRef.current = 'pending';
    }
  }, [isMobile]);

  // Load thread messages from API
  const loadThreadMessages = useCallback(async (threadId: string) => {
    try {
      console.log('📂 [THREAD NAVIGATION] Loading thread messages:', {
        threadId,
        tokenPresent: !!token,
        tokenPreview: token ? `${token.substring(0, 20)}...` : 'NO TOKEN'
      });
      clearChatSuggestionsRef.current?.();
      setMessages([]); // Clear current messages

      const apiUrl = `/api/threads/${threadId}/messages`;
      console.log('📂 [THREAD NAVIGATION] Making request to:', apiUrl);

      const response = await authorizedFetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      console.log('📂 [THREAD NAVIGATION] Response received:', {
        ok: response.ok,
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries(response.headers.entries())
      });

      if (response.ok) {
        const rawMessages = await response.json();
        console.log('📂 [THREAD NAVIGATION] Raw API response:', rawMessages);
        console.log('📂 [THREAD NAVIGATION] Loaded thread messages successfully:', {
          threadId,
          messageCount: rawMessages.length,
          messages: rawMessages
        });

        // Convert API messages to ChatMessage format
        const chatMessages = rawMessages.map((msg: any, index: number) => {
          console.log(`📂 [THREAD NAVIGATION] Converting message ${index}:`, msg);
          const converted = {
            id: msg.id || `msg-${Date.now()}-${index}`,
            content: msg.text || msg.content || '',
            isUser: msg.message_type === 'human' || msg.role === 'user',
            runId: msg.run_id || undefined,  // Include run_id for feedback buttons
            feedback: msg.feedback || undefined,  // Include feedback data if available
            messageType: msg.message_type || undefined,
            activity: msg.activity || undefined,
            objectUpdate: msg.object_update || undefined,
            attachments: Array.isArray(msg.attachments)
              ? msg.attachments.map((a: Record<string, unknown>) => ({
                  id: String(a.id),
                  original_name: String(a.original_name ?? ''),
                  mime_type: String(a.mime_type ?? ''),
                  status: String(a.status ?? ''),
                  file_url: a.file_url ? String(a.file_url) : undefined,
                  size_bytes: typeof a.size_bytes === 'number' ? a.size_bytes : undefined,
                  error: a.error != null ? String(a.error) : undefined,
                }))
              : undefined,
          };
          console.log(`📂 [THREAD NAVIGATION] Converted to:`, converted);
          return converted;
        });

        const mergedObjectUpdates = collapseObjectUpdateMessagesByRunId(
          chatMessages as ChatMessage[]
        );
        console.log('📂 [THREAD NAVIGATION] Final chat messages:', mergedObjectUpdates);
        setMessages(mergedObjectUpdates);
        const latestAssistant = [...mergedObjectUpdates]
          .reverse()
          .find((m: ChatMessage) => !m.isUser && !m.isDelta);
        const serverMessageId = latestAssistant ? resolveServerMessageId(latestAssistant) : undefined;
        if (serverMessageId) {
          void loadSuggestionsForThreadRef.current?.(threadId, {
            serverMessageId,
            runId: latestAssistant?.runId ?? null,
            anchorMessageId: latestAssistant?.id,
          });
        }
        return true; // Success
      } else {
        const errorText = await response.text();
        console.error('📂 [THREAD NAVIGATION] Failed to load thread messages:', {
          threadId,
          status: response.status,
          statusText: response.statusText,
          errorBody: errorText
        });
        setMessages([]);
        return false; // Failed to load
      }
    } catch (error) {
      console.error('📂 [THREAD NAVIGATION] Error loading thread messages:', {
        threadId,
        error: error instanceof Error ? error.message : String(error)
      });
      setMessages([]);
      return false; // Failed to load
    }
  }, []);

  const {
    currentThreadId,
    onBeforeSend,
    onThreadCreated,
    startNewConversation: startNewThreadSession,
    selectThread,
  } = useThreadController({
    searchParams,
    navigate,
    location,
    isLoadingRef,
    canLoadThreadMessages: isInitialized && isAuthenticated && agentAvailableForRoute,
    loadThreadMessages,
  });

  /** Single-flight: parallel uploads must not each POST /api/threads (different thread ids). */
  const ensureThreadInFlightRef = useRef<Promise<string | null> | null>(null);
  useEffect(() => {
    if (!currentThreadId) {
      ensureThreadInFlightRef.current = null;
    }
  }, [currentThreadId]);

  const ensureThread = useCallback(async (): Promise<string | null> => {
    if (currentThreadId) return currentThreadId;
    if (ensureThreadInFlightRef.current) {
      return ensureThreadInFlightRef.current;
    }
    const p = (async (): Promise<string | null> => {
      try {
        const response = await authorizedFetch('/api/threads', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ subject: 'New conversation' }),
        });
        if (!response.ok) return null;
        const t = await response.json();
        const id = String(t.id);
        selectThread(id);
        return id;
      } finally {
        ensureThreadInFlightRef.current = null;
      }
    })();
    ensureThreadInFlightRef.current = p;
    return p;
  }, [currentThreadId, selectThread]);

  /** Mobile: switching to chat must show the header immediately — scroll-hide on page can leave header false for one frame (glitch). */
  const commitMobilePane = useCallback((pane: 'chat' | 'page') => {
    setActiveMobilePane(pane);
    if (pane === 'chat') {
      appliedChromeVisibleRef.current = true;
      setIsMobileHeaderVisible(true);
    }
  }, []);

  const layout = (CURRENT_CLIENT as any).layout;
  const {
    openRoute,
    openThread,
    openThreadsIndex,
    applySurfaceIntent,
  } = useSurfaceNavigation({
    isMobile,
    layout,
    navigate,
    searchParams,
    setActiveMobilePane: commitMobilePane,
    selectThread,
  })

  const navigateInPlace = useCallback((path: string) => {
    suppressNextRouteSurfaceIntentRef.current = true;
    navigateWithThreadPreservation(navigate, path, searchParams);
  }, [navigate, searchParams]);

  useEffect(() => {
    if (previousPathnameRef.current === null) {
      previousPathnameRef.current = location.pathname;
      return;
    }

    if (
      agentAvailableForRoute &&
      isMobile &&
      previousPathnameRef.current !== location.pathname
    ) {
      if (suppressNextRouteSurfaceIntentRef.current) {
        suppressNextRouteSurfaceIntentRef.current = false;
        previousPathnameRef.current = location.pathname;
        return;
      }
      const prevPath = previousPathnameRef.current;
      if (!isMobileFocusBootstrap(prevPath, location.pathname)) {
        applySurfaceIntent('routeNavigation');
      }
    }

    previousPathnameRef.current = location.pathname;
  }, [applySurfaceIntent, isMobile, location.pathname, agentAvailableForRoute]);

  /** Reset nested scroll pane on route change; skip when agent drives focus via URL (see AGENT_HIGHLIGHT_PARAM). deps: pathname only — same-pathname highlight cleanup must not trigger reset. */
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get(AGENT_HIGHLIGHT_PARAM)?.trim()) return;

    const prevPath = listScrollPrevPathRef.current;
    listScrollPrevPathRef.current = location.pathname;

    const stateTop = readRestoreScrollTop(location.state);
    const listPath = listReturnPathFromLocation(location);
    const memoryTop =
      isJobsListPathname(location.pathname) && prevPath != null && isJobsDetailPathname(prevPath)
        ? takeRememberedListScroll(listPath)
        : undefined;
    const restoreTop = stateTop ?? memoryTop;

    const el = pageScrollRef.current;
    if (!el) return;

    if (restoreTop == null) {
      el.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      return;
    }

    const apply = () => {
      el.scrollTo({ top: restoreTop, left: 0, behavior: 'auto' });
    };
    apply();
    const observer = new ResizeObserver(() => {
      if (el.scrollHeight >= restoreTop) {
        apply();
        observer.disconnect();
      }
    });
    observer.observe(el);
    const timeoutId = window.setTimeout(() => {
      apply();
      observer.disconnect();
    }, 1500);
    return () => {
      observer.disconnect();
      window.clearTimeout(timeoutId);
    };
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps -- omit location.search so stripping agentHighlight on same pathname does not scroll-to-top

  useEffect(() => {
    if (isMobile) return;
    const params = new URLSearchParams(location.search);
    if (params.get(AGENT_HIGHLIGHT_PARAM)?.trim()) return;
    if (previousFocusPathRef.current === null) {
      previousFocusPathRef.current = location.pathname;
      return;
    }
    if (previousFocusPathRef.current === location.pathname) return;
    previousFocusPathRef.current = location.pathname;
    document.getElementById('main')?.focus({ preventScroll: true });
  }, [location.pathname, isMobile]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isPublicSharePath(location.pathname)) return
    publicShareScrollRef.current?.scrollTo({ top: 0, left: 0, behavior: 'auto' })
  }, [location.pathname])

  // Thread event callbacks - now simplified since most thread events come as UI deltas
  const threadCallbacks = {
    onThreadEvent: (event: {
      kind: 'thread_created' | 'thread_updated' | 'thread_title_updated' | 'thread_summary_updated'
      threadId: string
      subject?: string
    }) => {
      if (event.kind === 'thread_created') {
        console.log('🆕 [THREAD EVENT] Thread created:', event.threadId);
        onThreadCreated(event.threadId);
        queryClient.invalidateQueries({ queryKey: ['threads'] });
        return;
      }
      if (
        event.kind === 'thread_updated' ||
        event.kind === 'thread_title_updated' ||
        event.kind === 'thread_summary_updated'
      ) {
        console.log('🔄 [THREAD EVENT] Thread changed:', event.threadId, event.kind);
        queryClient.invalidateQueries({ queryKey: ['threads'] });
        queryClient.invalidateQueries({ queryKey: ['thread', event.threadId] });
        queryClient.invalidateQueries({ queryKey: ['thread-files', event.threadId] });
      }
    },
    onUIEvent: (data: any) => {
      console.log('🔄 [UI EVENT] Received UI update event:', data);
      if (data.action === 'navigate' && data.target) {
        console.log(`🔄 [UI EVENT] Navigating to: ${data.target}`);
        openRoute(data.target);
        // Also invalidate cache when navigating to profile - field highlighting handled by TanStack comparison
        if (data.target === '/profile') {
          console.log('🔄 [UI EVENT] Invalidating profile cache after navigation');
          queryClient.invalidateQueries({ queryKey: ['profile'] });
        }
      } else if (data.entity === 'profile' && data.action === 'updated') {
        console.log('🔄 [UI EVENT] Invalidating profile cache for real-time updates');
        queryClient.invalidateQueries({ queryKey: ['profile'] });
      } else if (data.entity === 'onboarding' && data.action === 'updated') {
        console.log('🔄 [UI EVENT] Invalidating onboarding status cache');
        queryClient.invalidateQueries({ queryKey: ['onboarding'] });
      } else if (data.entity === 'memory' && ['stored', 'retrieved'].includes(data.action)) {
        console.log('🧠 [UI EVENT] Memory event:', data.action, 'invalidating memories cache');
        queryClient.invalidateQueries({ queryKey: ['memories'] });
      } else {
        console.log(`🔄 [UI EVENT] Unhandled event: ${data.entity}.${data.action}`);
      }
    },
    onThreadTitleGenerating: (data: any) => {
      console.log('🎯 [APP] Thread title generating:', data);
      // Additional app-level handling if needed
    }
  };

  // Use SSE hook for all streaming functionality
  const {
    messages,
    isPersistentConnected,
    isLoading,
    sendMessage,
    cancelActiveMessage,
    startNewConversation,
    setMessages,
    capabilityMenuCategories,
    clearCapabilityMenu,
    stagedAttachments,
    uploadThreadAttachment,
    removeStagedAttachment,
    openNavigationIntent,
    cancelNavigationIntent,
    clearChatSuggestions,
    loadSuggestionsForThread,
  } = useSSE(
    () => {
      // Trigger animation when AI response starts
      if (animationTriggerRef.current) {
        animationTriggerRef.current();
      }
    }, 
    token, 
    agentAvailableForRoute, 
    threadCallbacks, 
    undefined, // searchCallbacks - not used in App.tsx
    currentThreadId,
    {
      navigate,
      openRoute,
      navigateInPlace,
      isMobile,
      queryClient,
      searchParams,
      onAuthFailure: () => authFailureHandlerRef.current(),
      ensureThread,
    }
  ); // Auto-connect with thread event callbacks, current thread, and navigation helpers

  const resetAgentChatSession = useCallback(() => {
    cancelActiveMessage();
    chatDraftRef.current = '';
    chatPaneRef.current?.setInput('');
    startNewThreadSession();
    startNewConversation();
  }, [cancelActiveMessage, startNewConversation, startNewThreadSession]);

  const handleLogout = useCallback(() => {
    resetAgentChatSession();
    logout();
  }, [logout, resetAgentChatSession]);

  authFailureHandlerRef.current = handleLogout;

  useEffect(() => {
    const prev = prevUserIdRef.current;
    const next = user?.id ?? null;
    if (shouldResetChatOnUserChange(prev, next)) {
      resetAgentChatSession();
    }
    prevUserIdRef.current = next;
  }, [user?.id, resetAgentChatSession]);

  const prevFocusForMenuRef = useRef<string | null>(null);
  useEffect(() => {
    const focus = userFocus?.current_focus ?? null;
    if (prevFocusForMenuRef.current !== null && prevFocusForMenuRef.current !== focus) {
      clearCapabilityMenu();
    }
    prevFocusForMenuRef.current = focus;
  }, [userFocus?.current_focus, clearCapabilityMenu]);

  useEffect(() => {
    loadSuggestionsForThreadRef.current = loadSuggestionsForThread;
    clearChatSuggestionsRef.current = clearChatSuggestions;
  }, [loadSuggestionsForThread, clearChatSuggestions]);

  useEffect(() => {
    if (!agentAvailableForRoute || !isAuthenticated || !token || !isInitialized) return;
    const raw = sessionStorage.getItem(UI_AGENT_MAGIC_LINK_KICKOFF_KEY);
    if (!raw) return;
    let message = '';
    try {
      const parsed = JSON.parse(raw) as { message?: string };
      message = (parsed.message || '').trim();
    } catch {
      sessionStorage.removeItem(UI_AGENT_MAGIC_LINK_KICKOFF_KEY);
      return;
    }
    if (!message) {
      sessionStorage.removeItem(UI_AGENT_MAGIC_LINK_KICKOFF_KEY);
      return;
    }

    // Keep sessionStorage until send fire: React Strict Mode dev runs mount→cleanup→mount and would
    // otherwise clear the key + cancel the timer on the first synthetic unmount, losing the kickoff.
    let cancelled = false;
    const t = window.setTimeout(() => {
      if (cancelled) return;
      sessionStorage.removeItem(UI_AGENT_MAGIC_LINK_KICKOFF_KEY);
      sendMessage(message);
    }, 450);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [agentAvailableForRoute, isAuthenticated, token, isInitialized, sendMessage]);

  useEffect(() => {
    isLoadingRef.current = isLoading;
  }, [isLoading]);

  // Handle focus changes from sidebar
  const handleFocusChange = useCallback((focusData: UserFocus | null) => {
    console.log('🎯 [FOCUS CHANGE] Focus updated from sidebar:', focusData);
    if (!focusData) return;
    setUserFocus((prev) => {
      if (!prev) return focusData;
      const sameCurrentFocus = prev.current_focus === focusData.current_focus;
      const sameAvailableFoci = (prev.available_foci || []).join(',') === (focusData.available_foci || []).join(',');
      return sameCurrentFocus && sameAvailableFoci ? prev : focusData;
    });
  }, []);

  // Handle composer submission — ChatPane owns the input text and passes it here.
  const handleSubmitMessage = useCallback((message: string): boolean => {
    if (!agentAvailableForRoute) return false;
    console.log('💬 [MESSAGE LIFECYCLE] Sending message:', {
      message: message.substring(0, 100) + (message.length > 100 ? '...' : ''),
      currentThreadId: currentThreadId,
      hasExistingThread: !!currentThreadId,
      willLetBackendCreateThread: !currentThreadId
    });
    onBeforeSend();
    // Let the backend create thread IDs - don't generate them in frontend
    // Pass null/undefined for new threads, existing thread_id for continuation
    return sendMessage(message, currentThreadId || undefined);
  }, [
    sendMessage,
    currentThreadId,
    onBeforeSend,
    agentAvailableForRoute,
  ]);

  const composerConfig = useMemo(
    () => resolveComposerConfig(CURRENT_CLIENT as Parameters<typeof resolveComposerConfig>[0]),
    []
  );
  const chatSuggestions = (CURRENT_CLIENT as {
    chatSuggestions?: Parameters<typeof resolveComposerStarters>[0];
  }).chatSuggestions;
  const onboardingComposer = useMemo(
    () =>
      resolveOnboardingComposer(
        onboardingStatus,
        chatSuggestions,
        userFocus?.current_focus,
        composerConfig.starterCount,
      ),
    [
      onboardingStatus,
      chatSuggestions,
      userFocus?.current_focus,
      composerConfig.starterCount,
    ],
  );
  const composerStarters = useMemo(
    () =>
      resolveComposerStarters(
        chatSuggestions,
        composerConfig.starters,
        userFocus?.current_focus,
        composerConfig.starterCount,
        onboardingStatus,
      ),
    [
      chatSuggestions,
      composerConfig.starters,
      composerConfig.starterCount,
      userFocus?.current_focus,
      onboardingStatus,
    ],
  );

  const effectiveCapabilityMenuCategories = useMemo(() => {
    const filterVisible = (cats: typeof capabilityMenuCategories) =>
      cats.filter((c) => (c.examples?.length ?? 0) > 0 || Boolean(c.streaming));

    if (capabilityMenuCategories.length > 0) {
      return filterVisible(
        filterComposerMenuCategories(capabilityMenuCategories, userFocus?.current_focus)
      );
    }
    const staticCategories = resolveStaticComposerMenuCategories(
      CURRENT_CLIENT as unknown as Parameters<typeof resolveStaticComposerMenuCategories>[0],
    );
    return filterVisible(
      filterComposerMenuCategories(staticCategories, userFocus?.current_focus)
    );
  }, [capabilityMenuCategories, userFocus?.current_focus]);

  const handleCapabilityExampleSelect = useCallback(
    (example: ComposerCapabilityExample): boolean => {
      if (!agentAvailableForRoute || !example.message.trim()) return false;
      const accepted = sendMessage(example.message, currentThreadId || undefined);
      if (accepted) {
        clearChatSuggestions();
      }
      return accepted;
    },
    [
      agentAvailableForRoute,
      clearChatSuggestions,
      currentThreadId,
      sendMessage,
    ]
  );

  const handleAttachFiles = useCallback(
    (files: FileList) => {
      void (async () => {
        for (const f of Array.from(files)) {
          await uploadThreadAttachment(f);
        }
      })();
    },
    [uploadThreadAttachment]
  );

  const handleSendSuggestion = useCallback((message: string): boolean => {
    if (!agentAvailableForRoute || !message.trim()) return false;
    const accepted = sendMessage(message, currentThreadId || undefined);
    if (accepted) {
      clearChatSuggestions();
    }
    return accepted;
  }, [agentAvailableForRoute, clearChatSuggestions, currentThreadId, sendMessage]);

  // Handle starting a new conversation (clears thread state)
  // Thread lifecycle: new thread = currentThreadId null; ID assigned by backend on first send
  // via thread_created event; persistence happens when first message is sent
  const handleNewConversation = useCallback(() => {
    if (!agentAvailableForRoute) return;
    console.log('🆕 [NEW CONVERSATION] Starting new conversation');
    applySurfaceIntent('newConversation');
    chatDraftRef.current = '';
    chatPaneRef.current?.setInput('');
    startNewThreadSession();
    startNewConversation();
  }, [applySurfaceIntent, startNewConversation, startNewThreadSession]);

  // Handle starting conversation from dashboard
  const handleStartConversation = useCallback((message: string) => {
    if (!agentAvailableForRoute) return;
    console.log('🎯 [DASHBOARD CONVERSATION] Starting conversation from dashboard:', {
      message: message.substring(0, 100) + (message.length > 100 ? '...' : ''),
      currentThreadId
    });
    chatDraftRef.current = message;
    chatPaneRef.current?.setInput(message);
    // Small delay to ensure input is set before submitting
    setTimeout(() => {
      const accepted = sendMessage(message, currentThreadId || undefined);
      if (accepted) {
        chatDraftRef.current = '';
        chatPaneRef.current?.setInput('');
      } else {
        console.log('🚫 [DASHBOARD CONVERSATION] Send not accepted; preserving input');
      }
    }, 100);
  }, [sendMessage]);

  // Handle thread selection from sidebar
  const handleThreadSelect = useCallback((threadId: string) => {
    console.log('📂 [THREAD NAVIGATION] Thread selected from sidebar:', {
      selectedThreadId: threadId,
      previousThreadId: currentThreadId,
      switchingThreads: threadId !== currentThreadId,
      currentPath: location.pathname,
      currentURL: window.location.href
    });

    // Thread selection should keep the current route but surface the chat pane on mobile.
    openThread(threadId);
  }, [currentThreadId, location.pathname, openThread]);

  const handleClearCurrentThread = useCallback(() => {
    if (!agentAvailableForRoute) return;
    console.log('🗑️ [THREAD CLEAR] Clearing current thread view');
    chatDraftRef.current = '';
    chatPaneRef.current?.setInput('');
    startNewThreadSession();
    startNewConversation();
  }, [startNewThreadSession, startNewConversation]);

  // Handle feedback submission
  const agentPanelValue = useMemo(
    () => ({
      available: agentAvailableForRoute && !isMobile,
      agentCollapsed,
      pageCollapsed,
      collapseAgent: () => {
        if (!agentCollapsed && !pageCollapsed && !isAnimating) {
          void handleAgentCollapsedToggle();
        }
      },
      showAgent: () => {
        if (agentCollapsed && !isAnimating) {
          void handleAgentCollapsedToggle();
        }
      },
      collapsePage: () => {
        if (!pageCollapsed && !isAnimating) {
          void handlePageCollapsedToggle();
        }
      },
    }),
    [
      agentAvailableForRoute,
      isMobile,
      agentCollapsed,
      pageCollapsed,
      isAnimating,
      handleAgentCollapsedToggle,
      handlePageCollapsedToggle,
    ]
  );

  const handleFeedback = useCallback(async (runId: string, score?: number, tags?: string[], comment?: string) => {
    console.log('👍 [FEEDBACK] Submitting feedback:', { runId, score, tags_count: tags?.length, comment_len: comment?.length });
    
    try {
      const response = await authorizedFetch('/api/feedback/message-rating', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ run_id: runId, score, tags, comment })
      });
      
      const result = await response.json();
      console.log('👍 [FEEDBACK] Response:', { success: result.success, message: result.message, error: result.error });
      
      if (result.success) {
        console.log('✅ [FEEDBACK] Submitted successfully');
        
        // Update message state to reflect feedback
        setMessages(prevMessages => 
          prevMessages.map(msg => 
            msg.runId === runId
              ? { ...msg, feedback: { score, tags, comment, at: new Date().toISOString() } }
              : msg
          )
        );
      } else {
        console.error('❌ [FEEDBACK] Failed:', result.error);
        toast.error(
          typeof result.error === 'string' && result.error.trim()
            ? result.error
            : 'Could not save feedback',
        )
      }
    } catch (error) {
      console.error('❌ [FEEDBACK] Request failed:', error);
      toast.error('Could not save feedback')
    }
  }, [setMessages]);

  const scrollToMobilePane = useCallback((pane: 'chat' | 'page') => {
    mobilePaneDragDeltaRef.current = 0;
    commitMobilePane(pane);
  }, [commitMobilePane]);

  const handleMobilePaneTouchStart = useCallback((e: React.TouchEvent<HTMLDivElement>) => {
    if (!isMobile) return;
    const touch = e.touches[0];
    mobilePaneTouchStartRef.current = { x: touch.clientX, y: touch.clientY };
    mobilePaneGestureRef.current = 'pending';
    mobilePaneDragDeltaRef.current = 0;
  }, [isMobile]);

  const handleMobilePaneTouchMove = useCallback((e: React.TouchEvent<HTMLDivElement>) => {
    if (!isMobile || !mobilePaneTouchStartRef.current) return;

    const touch = e.touches[0];
    const deltaX = touch.clientX - mobilePaneTouchStartRef.current.x;
    const deltaY = touch.clientY - mobilePaneTouchStartRef.current.y;
    const absX = Math.abs(deltaX);
    const absY = Math.abs(deltaY);

    if (mobilePaneGestureRef.current === 'pending') {
      if (absX < 8 && absY < 8) return;
      mobilePaneGestureRef.current = absX > absY ? 'horizontal' : 'vertical';
    }

    if (mobilePaneGestureRef.current !== 'horizontal') return;

    if (e.cancelable) {
      e.preventDefault();
    }

    const isPullingPastEdge =
      (activeMobilePane === 'chat' && deltaX > 0) ||
      (activeMobilePane === 'page' && deltaX < 0);

    mobilePaneDragDeltaRef.current = isPullingPastEdge ? deltaX * 0.35 : deltaX;
  }, [activeMobilePane, isMobile]);

  const handleMobilePaneTouchEnd = useCallback(() => {
    if (!isMobile || !mobilePaneTouchStartRef.current) return;

    const trackWidth = mobilePaneTrackRef.current?.clientWidth ?? 0;
    const swipeThreshold = Math.max(48, trackWidth * 0.18);
    const deltaX = mobilePaneDragDeltaRef.current;

    if (mobilePaneGestureRef.current === 'horizontal') {
      if (deltaX <= -swipeThreshold) {
        commitMobilePane('page');
      } else if (deltaX >= swipeThreshold) {
        commitMobilePane('chat');
      }
    }

    mobilePaneTouchStartRef.current = null;
    mobilePaneGestureRef.current = 'pending';
    mobilePaneDragDeltaRef.current = 0;
  }, [isMobile, commitMobilePane]);

  useEffect(() => {
    if (isSettingsRoute && isMobile) {
      commitMobilePane('page');
    }
  }, [isSettingsRoute, isMobile, commitMobilePane]);

  // Show loading until auth state is initialized from localStorage
  if (!isInitialized) {
    return (
      <ThemeProvider key="boot" defaultTheme="system" storageKey={APP_THEME_STORAGE_KEY}>
        <AppShellSkeleton ariaLabel="Loading application" />
      </ThemeProvider>
    );
  }

  // Yes Human Concepts — public marketing, no agent shell
  if (isYeshumanConceptsPath(location.pathname)) {
    return (
      <ThemeProvider
        key="yeshuman-concepts"
        defaultTheme="system"
        storageKey={APP_THEME_STORAGE_KEY}
        manageDocumentTheme={false}
      >
        <div className="h-full min-h-0 overflow-y-auto overscroll-y-contain touch-pan-y custom-scrollbar">
          <YeshumanConceptsRoutes />
        </div>
      </ThemeProvider>
    );
  }

  // Legal pages — public, no agent shell
  if (isLegalPath(location.pathname)) {
    return (
      <ThemeProvider
        key="legal"
        defaultTheme="system"
        storageKey={APP_THEME_STORAGE_KEY}
        manageDocumentTheme={false}
      >
        <div className="h-full min-h-0 overflow-y-auto overscroll-y-contain touch-pan-y custom-scrollbar">
          <LegalRoutes />
        </div>
      </ThemeProvider>
    );
  }

  // Shareable public artifact pages — no agent shell (logged in or out)
  if (isPublicSharePath(location.pathname)) {
    return (
      <ThemeProvider
        key={isAuthenticated ? 'share-auth' : 'share-guest'}
        defaultTheme="system"
        storageKey={APP_THEME_STORAGE_KEY}
        manageDocumentTheme={false}
      >
        <div
          ref={publicShareScrollRef}
          key={location.pathname}
          className="h-full min-h-0 overflow-y-auto overscroll-y-contain touch-pan-y custom-scrollbar"
        >
          <PublicShareRoutes />
        </div>
      </ThemeProvider>
    );
  }

  // If not authenticated, show login page and public auth flows
  if (!isAuthenticated) {
    return (
      <ThemeProvider
        key="guest"
        defaultTheme="system"
        storageKey={APP_THEME_STORAGE_KEY}
        manageDocumentTheme={false}
      >
        <div className="h-full min-h-0 overflow-y-auto overscroll-y-contain touch-pan-y custom-scrollbar">
          <Routes>
          <Route
            path="/"
            element={
              resolveGuestIndex(CURRENT_CLIENT as ClientWithLanding) === 'login' ? (
                <Navigate to="/login" replace />
              ) : (
                <PublicLandingPage />
              )
            }
          />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register/check-email" element={<CheckEmailPage />} />
          <Route path="/register/pending" element={<SignupPendingPage />} />
          <Route path="/register" element={<SignupPage />} />
          <Route path="/auth/reset-password" element={
            <AuthPageShell title="Reset password">
              <PasswordResetRequest />
            </AuthPageShell>
          } />
          <Route path="/auth/reset-password/:key" element={
            <AuthPageShell title="Set a new password">
              <PasswordResetConfirm />
            </AuthPageShell>
          } />
          <Route path="/auth/verify-email/*" element={
            <AuthPageShell title="Verify email">
              <EmailVerify />
            </AuthPageShell>
          } />
          <Route path="/auth/invite/:key" element={<InviteAccept />} />
          <Route path="/auth/provider/callback" element={<AuthProviderCallback />} />
          <Route path="/auth/provider/signup" element={<AuthProviderSignup />} />
          <Route path="/legal/privacy" element={<LegalRoutes />} />
          <Route path="/legal/terms" element={<LegalRoutes />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </div>
      </ThemeProvider>
    );
  }

  const sidebarDefaultOpen = layout?.sidebarDefaultOpen ?? false;
  const contentInset = layout?.contentInset !== false;
  const headerFullWidth = layout?.headerFullWidth === true;
  const showAppHeader = true;
  const memoriesThreadsInHeader = layout?.memoriesThreadsInHeader === true;
  const threadsHeaderDesktopLabeled = layout?.threadsHeaderDesktopLabeled === true;
  const newConversationHeaderIconRaw = layout?.newConversationHeaderIcon as string | undefined;
  const HeaderNewConvIcon = newConversationHeaderIconRaw === 'plus' ? SquarePen : MessageSquarePlus;
  const newChatCentered = layout?.newChatCentered === true;
  const chatSuggestionsConfig = (CURRENT_CLIENT as any).features?.chatSuggestions;
  const chatSuggestionsEnabled = chatSuggestionsConfig?.enabled !== false;
  const chatSuggestionsMaxPills =
    typeof chatSuggestionsConfig?.maxPills === 'number' ? chatSuggestionsConfig.maxPills : 4;
  // Omit key = show on mobile; set false to hide (e.g. minimal chrome clients).
  const showMobileSidebarMenuInHeader = layout?.showMobileSidebarMenuInHeader !== false;
  const mobileChatActionsInMenu = isMobile;
  const mobileInviteInMenu = isMobile;

  const sidebarCollapsibleNone = layout?.sidebarCollapsible === false;
  const showMobileSidebarMenuButton =
    isMobile &&
    showMobileSidebarMenuInHeader &&
    !sidebarCollapsibleNone;
  const insetClassName = contentInset ? '!mt-2 !mr-2 !mb-2' : '';
  /** Mobile: stable layout viewport height; avoid visualViewport pixel height (conflicts with iOS focus scroll). */
  const mobileShellHeight = '100dvh';
  /** Mobile + headerFullWidth: don’t fix SidebarInset to `100dvh - 3.5rem` — that blocked reclaiming space when the header row collapses (flex fills below a shrink-0 header). */
  const insetStyle = isMobile
    ? contentInset
      ? { height: `calc(${mobileShellHeight} - 16px)`, maxHeight: `calc(${mobileShellHeight} - 16px)` }
      : headerFullWidth
        ? {}
        : { height: mobileShellHeight, maxHeight: mobileShellHeight }
    : contentInset
      ? { height: 'calc(100vh - 16px)', maxHeight: 'calc(100vh - 16px)' }
      : headerFullWidth && showAppHeader
        ? { height: 'calc(100svh - 3.5rem)', maxHeight: 'calc(100svh - 3.5rem)' }
        : { height: '100vh', maxHeight: '100vh' };

  const headerBackgroundPrimary = layout?.headerBackgroundPrimary === true;
  const showBgInApp = layout?.showBackgroundInApp === true;
  const clientTheme = CURRENT_CLIENT.theme as ClientTheme | undefined;
  /** When unset, `--page-background` is `var(--background)`; `bg-[var(--page-background)]/50` opacity is unreliable in Tailwind. Some packs set `pageBackground` explicitly. */
  const usesExplicitPageSurface = Boolean(
    clientTheme?.colors?.pageBackground || clientTheme?.dark?.colors?.pageBackground
  );
  /**
   * Light glass (`/10`) for clients without an explicit page token so `ClientBackground` tint
   * shows through; avoids a chalky chip vs the page column.
   */
  const defaultGlassSurface = '!bg-background/10 backdrop-blur-sm';
  const headerBgClass = isMobile
    ? headerBackgroundPrimary
      ? 'bg-primary-dark text-primary-dark-foreground'
      : 'bg-background text-foreground'
    : showBgInApp
    ? (headerBackgroundPrimary
        ? '!bg-primary-dark/50 backdrop-blur-sm'
        : usesExplicitPageSurface
          ? '!bg-background/50 backdrop-blur-sm'
          : defaultGlassSurface)
    : (headerBackgroundPrimary ? 'bg-primary-dark' : (headerFullWidth ? 'bg-background' : ''));
  const headerTextClass = headerBackgroundPrimary ? 'text-primary-dark-foreground' : '';
  const mobileControlSurfaceClass = headerBackgroundPrimary
    ? 'bg-primary-dark-foreground/10'
    : 'bg-muted';
  const headerBtnClass = isMobile
    ? headerBackgroundPrimary
      ? `${mobileControlSurfaceClass} text-primary-dark-foreground hover:bg-primary-dark-foreground/20`
      : `${mobileControlSurfaceClass} text-foreground/75 hover:bg-muted/80 hover:text-foreground`
    : headerBackgroundPrimary
      ? 'bg-white/20 hover:bg-white/30 text-primary-dark-foreground'
      : 'bg-transparent hover:bg-muted';
  /** Native header controls: align with `Button` size="sm" (h-8, text-xs, radius-button). */
  const headerChromeIconBtn = cn(
    'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-[var(--radius-button)] text-xs font-medium leading-none transition-colors [&_svg]:h-4 [&_svg]:w-4',
    headerBtnClass,
    (mobileAgentDrawer || mobileAgentSwipe) && 'h-11 w-11 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2'
  );
  const headerChromeLabeledBtn = cn(
    'inline-flex h-8 shrink-0 items-center gap-2 px-3 rounded-[var(--radius-button)] text-xs font-medium leading-none transition-colors [&_svg]:h-4 [&_svg]:w-4',
    headerBtnClass
  );
  const mobilePaneSwitchBtnClass = isMobile
    ? headerBackgroundPrimary
      ? 'text-primary-dark-foreground/75 hover:text-primary-dark-foreground'
      : 'text-foreground/75 hover:text-foreground'
    : headerBackgroundPrimary
      ? 'text-primary-dark-foreground/70 hover:text-primary-dark-foreground'
      : 'text-foreground/75 hover:text-foreground';
  const mobilePaneSwitchActiveClass = isMobile
    ? headerBackgroundPrimary
      ? 'bg-primary-dark-foreground text-primary-dark shadow-sm'
      : 'bg-background text-foreground shadow-sm'
    : headerBackgroundPrimary
      ? 'bg-white text-primary-dark shadow-sm'
      : 'bg-background text-foreground shadow-sm';
  const headerInviteBtnEmphasis = cn(
    mobilePaneSwitchActiveClass,
    headerBackgroundPrimary
      ? 'ring-1 ring-black/10 hover:bg-white/90'
      : 'ring-1 ring-border hover:bg-muted',
  );
  const renderMobilePaneSwitch = (className?: string) => (
    <div
      role="group"
      aria-label="Chat and page view"
      className={cn(
        'inline-flex items-center gap-0.5 rounded-[calc(var(--radius-button)+2px)] p-0.5',
        isMobile ? mobileControlSurfaceClass : headerBackgroundPrimary ? 'bg-white/15' : 'bg-muted',
        className
      )}
    >
      <button
        type="button"
        onClick={() => scrollToMobilePane('chat')}
        className={cn(
          'flex items-center justify-center rounded-[var(--radius-button)] p-2 transition-[color,background-color,box-shadow,transform] active:scale-[0.96]',
          isMobile && 'h-11 w-11 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2',
          activeMobilePane === 'chat' ? mobilePaneSwitchActiveClass : mobilePaneSwitchBtnClass
        )}
        aria-pressed={activeMobilePane === 'chat'}
        aria-label="Chat"
        title="Chat"
      >
        <Sparkles className="h-4 w-4 shrink-0" aria-hidden />
      </button>
      <button
        type="button"
        onClick={() => scrollToMobilePane('page')}
        className={cn(
          'flex items-center justify-center rounded-[var(--radius-button)] p-2 transition-[color,background-color,box-shadow,transform] active:scale-[0.96]',
          isMobile && 'h-11 w-11 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2',
          activeMobilePane === 'page' ? mobilePaneSwitchActiveClass : mobilePaneSwitchBtnClass
        )}
        aria-pressed={activeMobilePane === 'page'}
        aria-label="Page"
        title="Page"
      >
        <Form className="h-4 w-4 shrink-0" aria-hidden />
      </button>
    </div>
  );

  const headerContent = (
    <div className={cn(
      'relative px-3 flex justify-between items-center flex-shrink-0 w-full',
      isMobile ? 'h-16' : 'h-14 border-b',
      headerBgClass,
    )}>
      <div className={cn('flex min-w-0 items-center gap-2', (mobileAgentDrawer || mobileAgentSwipe) && 'flex-1', headerTextClass)}>
        <MobileSidebarMenuButton
          visible={showMobileSidebarMenuButton}
          className={cn(
            headerBtnClass,
            '[&_svg]:h-4 [&_svg]:w-4',
            isMobile && 'h-11 w-11',
          )}
        />
        {!isMobile && <AnimatedTitle
          onAnimationTrigger={(triggerFn) => {
            animationTriggerRef.current = triggerFn;
          }}
          headerOnPrimary={headerBackgroundPrimary}
        />}
      </div>
      {agentAvailableForRoute && isMobile && renderMobilePaneSwitch('absolute left-1/2 -translate-x-1/2')}
      <div className={cn(
        'flex items-center',
        isMobile ? 'space-x-2' : 'space-x-4',
        headerBackgroundPrimary ? 'text-primary-dark-foreground' : ''
      )}>
        {isAuthenticated && !mobileInviteInMenu && (
          <ProductInviteHeaderButton
            isAuthenticated={isAuthenticated}
            iconButtonClassName={headerChromeIconBtn}
            labeledButtonClassName={headerChromeLabeledBtn}
            emphasisClassName={headerInviteBtnEmphasis}
            showLabel={!isMobile}
          />
        )}
        {agentAvailableForRoute && !mobileAgentDrawer && !mobileChatActionsInMenu && memoriesThreadsInHeader && (
          isMobile || !threadsHeaderDesktopLabeled ? (
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={openThreadsIndex}
                className={headerChromeIconBtn}
                aria-label="Threads"
                title="Threads"
              >
                <MessageSquare className="h-4 w-4 shrink-0" />
              </button>
              <button
                type="button"
                onClick={handleNewConversation}
                className={headerChromeIconBtn}
                aria-label="Start new conversation"
                title="Start new conversation"
              >
                <HeaderNewConvIcon className="h-4 w-4 shrink-0" />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={openThreadsIndex}
                className={headerChromeLabeledBtn}
                title="Threads"
              >
                <MessageSquare className="h-4 w-4 shrink-0" />
                <span>Threads</span>
              </button>
              <button
                type="button"
                onClick={handleNewConversation}
                className={headerChromeIconBtn}
                aria-label="Start new conversation"
                title="Start new conversation"
              >
                <HeaderNewConvIcon className="h-4 w-4 shrink-0" />
              </button>
            </div>
          )
        )}
        {agentAvailableForRoute && !mobileAgentDrawer && !mobileChatActionsInMenu && !memoriesThreadsInHeader && (
          <button
            type="button"
            onClick={handleNewConversation}
            className={headerChromeIconBtn}
            title="Start new conversation"
            aria-label="Start new conversation"
          >
            <HeaderNewConvIcon className="h-4 w-4 shrink-0" />
          </button>
        )}
        {isAuthenticated && !isMobile && isProductFeedbackHeaderIconEnabled() && (
          <FeedbackHeaderButton buttonClassName={headerChromeIconBtn} />
        )}
        {isAuthenticated && (
          <NotificationBell buttonClassName={headerChromeIconBtn} />
        )}
      </div>
    </div>
  );

  const mobileChatPaneInsetStyle: CSSProperties | undefined =
    isMobile && agentAvailableForRoute && !mobileAgentDrawer
      ? {
          // Safari: do not max() with env(keyboard-inset-height) — WebKit can report a bogus value and
          // stack with --ui-keyboard-bottom-inset (~¾ screen padding). Chrome keeps keyboard env.
          paddingBottom: isIOSSafari()
            ? // Safari: extra bottom padding on the chat column; cap so the textarea stays near the keyboard.
              'min(calc(env(safe-area-inset-bottom, 0px) + var(--ui-keyboard-bottom-inset, 0px)), 68px, 12dvh)'
            : 'calc(env(safe-area-inset-bottom, 0px) + max(env(keyboard-inset-height, 0px), var(--ui-keyboard-bottom-inset, 0px)))',
        }
      : undefined

  const isNewEmptyThread =
    agentAvailableForRoute &&
    !currentThreadId &&
    messages.length === 0;
  // Welcome empty-chat vs in-chat thread dock — see DESIGN_SYSTEM § Chat composer modes.
  const showCenteredEmptyChat = isNewEmptyThread && newChatCentered;

  const chatPaneContentConstrained =
    agentAvailableForRoute && !isMobile && pageCollapsed;
  const chatContentBandClassName = cn(
    'w-full',
    chatPaneContentConstrained && 'mx-auto max-w-[756px]'
  );
  const chatAttachFiles =
    agentAvailableForRoute && isAuthenticated ? handleAttachFiles : undefined;

  const chatPaneContent = (
    <div
      className={cn(
        'relative h-full min-h-0 min-w-0 flex flex-col overflow-hidden',
        showBgInApp &&
          (usesExplicitPageSurface ? '!bg-background/50 backdrop-blur-sm' : defaultGlassSurface)
      )}
      style={mobileChatPaneInsetStyle}
    >
      {agentAvailableForRoute && !isMobile && pageCollapsed ? (
        <Button
          type="button"
          variant="ghostToolbar"
          size="iconSm"
          onClick={handlePageCollapsedToggle}
          className="absolute right-3 top-6 z-10"
          title="Show page"
          aria-label="Show page"
        >
          <PanelRightOpen className="h-4 w-4 shrink-0" aria-hidden />
        </Button>
      ) : null}
      <div className="flex h-full min-h-0 w-full min-w-0 flex-col overflow-hidden">
        <ChatPane
          ref={chatPaneRef}
          draftRef={chatDraftRef}
          compactWelcome={mobileAgentDrawer && drawerKeyboardOpen}
          showCenteredEmptyChat={showCenteredEmptyChat}
          agentAvailableForRoute={agentAvailableForRoute}
          isAuthenticated={isAuthenticated}
          clientName={CURRENT_CLIENT.name}
          messages={messages}
          isLoading={isLoading}
          isPersistentConnected={isPersistentConnected}
          composerConfig={composerConfig}
          composerStarters={composerStarters}
          onboardingComposer={onboardingComposer}
          capabilityMenuCategories={effectiveCapabilityMenuCategories}
          focusKey={userFocus?.current_focus}
          stagedAttachments={stagedAttachments}
          chatContentBandClassName={chatContentBandClassName}
          chatSuggestionsEnabled={chatSuggestionsEnabled}
          chatSuggestionsMaxPills={chatSuggestionsMaxPills}
          onSubmitMessage={handleSubmitMessage}
          onCancelActiveMessage={cancelActiveMessage}
          onSendCapabilityExample={handleCapabilityExampleSelect}
          onSendSuggestion={handleSendSuggestion}
          onAttachFiles={chatAttachFiles}
          onRemoveStagedAttachment={removeStagedAttachment}
          onFeedback={handleFeedback}
          onOpenNavigationIntent={openNavigationIntent}
          onCancelNavigationIntent={cancelNavigationIntent}
        />
      </div>
    </div>
  );

  const pagePaneContent = (
    <div
      ref={setPagePaneEl}
      className={cn(
        "relative h-full min-h-0 min-w-0 flex flex-col overflow-hidden text-[var(--page-foreground)]",
        showBgInApp
          ? usesExplicitPageSurface
            ? "!bg-[var(--page-background)]/50 backdrop-blur-sm"
            : defaultGlassSurface
          : "bg-[var(--page-background)]"
      )}
    >
        <PagePaneLayoutProvider>
          <PagePaneScrollRegion
            scrollRef={pageScrollRef}
            className={mobileAgentDrawer ? 'pb-20 scroll-pb-20' : undefined}
          >
              <PluginPageCanvasProvider>
              <Routes>
              <Route path="/onboarding" element={<OnboardingWizard />} />
              <Route path="/profile/top-skills/:skillId" element={<TopSkillDetailPage />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/settings/*" element={<SettingsRoutes />} />
              <Route path="/notifications" element={<NotificationsPage />} />
              {isProductFeedbackEnabled() && (
                <Route
                  path="/feedback"
                  element={
                    <FeedbackPage
                      onStartConversation={agentAvailableForRoute ? handleStartConversation : undefined}
                    />
                  }
                />
              )}
              <Route path="/files" element={<UserFilesPage />} />
              {isDocumentsEnabled() && (
                <>
                  <Route path="/documents" element={<DocumentsPage />} />
                  <Route path="/documents/new" element={<DocumentNewPage />} />
                  <Route path="/documents/:documentId" element={<DocumentPage />} />
                </>
              )}
              {[
                ...getPluginSlots().pluginRoutes,
                ...getPluginSlots().focusRegistrations.flatMap((reg) => reg.routes ?? []),
                ...pluginCanvasPageRoutes(),
              ]
                .filter((route) => route.auth !== false)
                .map((route) => {
                  const Page = route.element
                  return <Route key={route.path} path={route.path} element={<Page />} />
                })}
              {AGENT_UI_ENABLED ? (
                <>
                  <Route path="/memories" element={<MemoriesPage />} />
                  <Route
                    path="/threads"
                    element={
                      <ThreadsPage
                        chatFocusRoute={getFocusRoute(userFocus?.current_focus ?? null)}
                        onOpenInAgent={openThread}
                      />
                    }
                  />
                  <Route
                    path="/threads/:threadId"
                    element={
                      <ThreadDetailPage
                        onOpenInAgent={openThread}
                      />
                    }
                  />
                </>
              ) : (
                <>
                  <Route
                    path="/memories"
                    element={
                      userFocus === null ? (
                        <DashboardPageSkeleton focus="generic" />
                      ) : (
                        <Navigate to={getFocusRoute(userFocus.current_focus)} replace />
                      )
                    }
                  />
                  <Route
                    path="/threads"
                    element={
                      userFocus === null ? (
                        <DashboardPageSkeleton focus="generic" />
                      ) : (
                        <Navigate to={getFocusRoute(userFocus.current_focus)} replace />
                      )
                    }
                  />
                </>
              )}

              {/* Focus-specific nested routes */}
              {getPluginSlots()
                .focusRegistrations.filter((reg) => reg.tree)
                .map((reg) => {
                  const Tree = reg.tree!
                  const mount = reg.path ?? `/${reg.focus}`
                  return (
                    <Route
                      key={mount}
                      path={`${mount}/*`}
                      element={
                        !isAuthenticated ? (
                          <NavigateWithThread to={mount} />
                        ) : userFocus === null ? (
                          <DashboardPageSkeleton focus={reg.skeleton ?? 'generic'} />
                        ) : (
                          <FocusRouteGate
                            routeFocus={reg.focus}
                            userFocus={userFocus}
                            onFocusChange={handleFocusChange}
                            pending={<DashboardPageSkeleton focus={reg.skeleton ?? 'generic'} />}
                            fallback={<NavigateWithThread to={getFocusRoute(userFocus.current_focus)} />}
                          >
                            <Tree onStartConversation={handleStartConversation} />
                          </FocusRouteGate>
                        )
                      }
                    />
                  )
                })}

              <Route path="/platform/*" element={
                !isAuthenticated ? (
                  <NavigateWithThread to="/platform" />
                ) : userFocus === null ? (
                  <DashboardPageSkeleton focus="generic" />
                ) : (
                  <FocusRouteGate
                    routeFocus="platform"
                    userFocus={userFocus}
                    onFocusChange={handleFocusChange}
                    pending={<DashboardPageSkeleton focus="generic" />}
                    fallback={<NavigateWithThread to={getFocusRoute(userFocus.current_focus)} />}
                  >
                    <PlatformRoutes onStartConversation={handleStartConversation} />
                  </FocusRouteGate>
                )
              } />

              {(['client', 'engineer', 'principal'] as const).map((agencyFocus) => (
                <Route
                  key={agencyFocus}
                  path={`/${agencyFocus}/*`}
                  element={
                    !isAuthenticated ? (
                      <NavigateWithThread to={`/${agencyFocus}`} />
                    ) : userFocus === null ? (
                      <DashboardPageSkeleton focus="generic" />
                    ) : (
                      <FocusRouteGate
                        routeFocus={agencyFocus}
                        userFocus={userFocus}
                        onFocusChange={handleFocusChange}
                        pending={<DashboardPageSkeleton focus="generic" />}
                        fallback={<NavigateWithThread to={getFocusRoute(userFocus.current_focus)} />}
                      >
                        <AgencyRoutes
                          focus={agencyFocus}
                          onStartConversation={handleStartConversation}
                        />
                      </FocusRouteGate>
                    )
                  }
                />
              ))}

              <Route path="/developer/*" element={
                !isAuthenticated ? (
                  <NavigateWithThread to="/developer" />
                ) : userFocus === null ? (
                  <DashboardPageSkeleton focus="generic" />
                ) : (
                  <FocusRouteGate
                    routeFocus="developer"
                    userFocus={userFocus}
                    onFocusChange={handleFocusChange}
                    pending={<DashboardPageSkeleton focus="generic" />}
                    fallback={<NavigateWithThread to={getFocusRoute(userFocus.current_focus)} />}
                  >
                    <DeveloperRoutes onStartConversation={handleStartConversation} />
                  </FocusRouteGate>
                )
              } />

              {/* Root redirect to current focus */}
              <Route path="/" element={
                userFocus ? (
                  <NavigateWithThread to={getFocusRoute(userFocus.current_focus)} />
                ) : (
                  <NavigateWithThread to={getFocusRoute(null)} />
                )
              } />

              {/* Catch-all redirect */}
              <Route path="*" element={
                userFocus === null ? (
                  <DashboardPageSkeleton focus="generic" />
                ) : (
                  <NavigateWithThread to={getFocusRoute(userFocus?.current_focus ?? null)} />
                )
              } />
              </Routes>
              </PluginPageCanvasProvider>
          </PagePaneScrollRegion>
        </PagePaneLayoutProvider>
    </div>
  );

  const passwordBanner =
    isAuthenticated && user?.has_usable_password === false ? (
      <PasswordHintBanner className="border-b border-border bg-muted/40 px-4 py-2" />
    ) : null;

  const mainContent = !agentAvailableForRoute ? (
    <div className="relative flex h-full w-full min-w-0 flex-1 flex-col overflow-hidden">
      {passwordBanner}
      {pagePaneContent}
    </div>
  ) : mobileAgentDrawer ? (
    <div className="relative flex h-full w-full min-w-0 flex-1 flex-col overflow-hidden">
      {passwordBanner}
      <div className="flex min-h-0 flex-1 flex-col">{pagePaneContent}</div>
      <MobileAgentDrawer
        onComposerFocus={() => chatPaneRef.current?.focus()}
        launcher={layout?.mobileAgentLauncher === 'composer' ? (
          <MobileAgentComposer draftRef={chatDraftRef} composerConfig={composerConfig}
            clientName={CURRENT_CLIENT.name} isLoading={isLoading}
            onSend={handleSubmitMessage} onOpen={() => commitMobilePane('chat')} />
        ) : undefined}
        showActions={!mobileChatActionsInMenu}
        open={activeMobilePane === 'chat'}
        onOpenChange={(open) => commitMobilePane(open ? 'chat' : 'page')}
        onKeyboardChange={setDrawerKeyboardOpen}
        onThreads={openThreadsIndex}
        onNewChat={handleNewConversation}
      >
        {chatPaneContent}
      </MobileAgentDrawer>
    </div>
  ) : isMobile ? (
    <div className="relative flex h-full w-full min-w-0 flex-1 flex-col overflow-hidden">
      {passwordBanner}
      <div
        ref={mobilePaneTrackRef}
        onTouchStart={handleMobilePaneTouchStart}
        onTouchMove={handleMobilePaneTouchMove}
        onTouchEnd={handleMobilePaneTouchEnd}
        onTouchCancel={handleMobilePaneTouchEnd}
        className="relative h-full w-full min-h-0 min-w-0 flex-1 overflow-hidden"
      >
        <div
          className={cn(
            'absolute inset-0 h-full min-h-0 w-full overflow-hidden',
            mobileAgentSwipe
              ? cn('transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:transition-none',
                  activeMobilePane === 'chat' ? 'translate-x-0' : '-translate-x-full pointer-events-none')
              : activeMobilePane === 'chat' ? 'z-10 opacity-100' : 'z-0 opacity-0 pointer-events-none'
          )}
          inert={activeMobilePane !== 'chat'}
          aria-hidden={activeMobilePane !== 'chat'}
        >
          {chatPaneContent}
        </div>
        <div
          className={cn(
            'absolute inset-0 h-full min-h-0 w-full overflow-hidden',
            mobileAgentSwipe
              ? cn('transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:transition-none',
                  activeMobilePane === 'page' ? 'translate-x-0' : 'translate-x-full pointer-events-none')
              : activeMobilePane === 'page' ? 'z-10 opacity-100' : 'z-0 opacity-0 pointer-events-none'
          )}
          inert={activeMobilePane !== 'page'}
          aria-hidden={activeMobilePane !== 'page'}
        >
          {pagePaneContent}
        </div>
      </div>
    </div>
  ) : (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      {passwordBanner}
      <ResizablePanelGroup
        groupRef={groupRef}
        id="chat-page-split"
        className="min-h-0 flex-1"
        disableCursor
        orientation="horizontal"
        defaultLayout={
          pageCollapsed
            ? { chat: 100, page: 0 }
            : agentCollapsed
              ? { chat: 0, page: 100 }
              : { chat: chatPageSizes[0], page: chatPageSizes[1] }
        }
        onLayoutChanged={handleChatPageLayoutChanged}
      >
        <ResizablePanel
          id="chat"
          defaultSize={pageCollapsed ? 100 : agentCollapsed ? 0 : chatPageSizes[0]}
          minSize={pageCollapsed || agentCollapsed || isAnimating ? 0 : 280}
          maxSize={agentCollapsed ? 0 : undefined}
        >
          {chatPaneContent}
        </ResizablePanel>
        {!agentCollapsed && !pageCollapsed && <ResizableHandle withHandle />}
        <ResizablePanel
          id="page"
          defaultSize={pageCollapsed ? 0 : chatPageSizes[1]}
          minSize={pageCollapsed || isAnimating ? 0 : 320}
          maxSize={pageCollapsed ? 0 : undefined}
        >
          {pagePaneContent}
        </ResizablePanel>
      </ResizablePanelGroup>
    </div>
  );

  const disableSidebarKeyboardShortcut = layout?.disableSidebarKeyboardShortcut === true;
  const sidebarProviderClassName = cn(
    isMobile ? 'h-[100dvh] min-h-0' : 'min-h-svh h-svh',
    headerFullWidth && showAppHeader && 'flex-col'
  );

  // The transparent mobile header shares the active pane's surface, including
  // layouts where the header is inside SidebarInset rather than above it.
  const mobileSurfaceClass = showBgInApp
    ? 'bg-transparent'
    : !agentAvailableForRoute || mobileAgentDrawer || activeMobilePane === 'page'
      ? 'bg-[var(--page-background)]'
      : 'bg-background';

  const sidebarInsetClass = cn(
    "flex h-full min-h-0 min-w-0 flex-col overflow-hidden",
    isMobile && mobileSurfaceClass,
    insetClassName,
    // Inset tenants (e.g. Yes Human): single frame on #main — peer-data inset border utilities do not reliably compile.
    contentInset && !isMobile && "border border-border",
    showBgInApp && "!bg-transparent overflow-hidden"
  );

  /** Mobile page chrome: outer clips height + inner slides vertically — keep both `duration-[750ms]` literals in sync when tuning speed. */
  const mobileHeaderChromeOuterClass = isMobile
    ? cn(
        'shrink-0 overflow-hidden transition-[max-height] duration-[750ms] ease-in-out motion-reduce:transition-none motion-reduce:duration-0',
        (isMobile || isMobileHeaderVisible) ? 'max-h-[5.5rem]' : 'max-h-0 pointer-events-none select-none'
      )
    : 'shrink-0';
  const mobileHeaderChromeInnerClass = isMobile
    ? cn(
        'transition-transform duration-[750ms] ease-in-out motion-reduce:transition-none motion-reduce:duration-0',
        (isMobile || isMobileHeaderVisible) ? 'translate-y-0' : '-translate-y-full'
      )
    : '';

  const skipToMainLink = (
    <a
      href="#main"
      className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-50 focus:rounded-md focus:bg-background focus:px-3 focus:py-2 focus:text-sm focus:ring-2 focus:ring-ring"
    >
      Skip to main content
    </a>
  );

  const sidebarShell = headerFullWidth ? (
    <>
      {showAppHeader && (
        <div
          className={mobileHeaderChromeOuterClass}
        >
          {isMobile ? (
            <div className={mobileHeaderChromeInnerClass}>{headerContent}</div>
          ) : (
            headerContent
          )}
        </div>
      )}
      <ArtifactCanvasProvider portalContainer={pagePanePortalHost}>
        <div
          className={cn(
            "flex flex-1 min-h-0 w-full",
            !showAppHeader && "h-full"
          )}
        >
          <AppSidebar
            agentUiEnabled={agentAvailableForRoute}
            onNavigateToPath={openRoute}
            onNavigateBack={navigateInPlace}
            onThreadSelect={handleThreadSelect}
            currentThreadId={currentThreadId}
            onClearCurrentThread={handleClearCurrentThread}
            onNewChat={handleNewConversation}
            onOpenThreads={openThreadsIndex}
            onFocusChange={handleFocusChange}
            threadCallbacks={threadCallbacks}
            activeMobilePane={isMobile ? activeMobilePane : undefined}
            onRestoreMobilePane={commitMobilePane}
            onLogout={handleLogout}
          />
          <SidebarInset id="main" tabIndex={-1} className={sidebarInsetClass} style={showBgInApp ? { ...insetStyle, backgroundColor: 'transparent' } : insetStyle}>
            {showBgInApp && (
              <ClientBackground className="absolute inset-0 z-0 w-full h-full min-w-full min-h-full object-cover pointer-events-none opacity-75 dark:opacity-70" />
            )}
            <div className={cn("relative flex flex-col flex-1 min-h-0", showBgInApp && "z-10")}>
              {mainContent}
            </div>
          </SidebarInset>
        </div>
      </ArtifactCanvasProvider>
    </>
  ) : (
    <ArtifactCanvasProvider portalContainer={pagePanePortalHost}>
      <AppSidebar
        agentUiEnabled={agentAvailableForRoute}
        onNavigateToPath={openRoute}
        onNavigateBack={navigateInPlace}
        onThreadSelect={handleThreadSelect}
        currentThreadId={currentThreadId}
        onClearCurrentThread={handleClearCurrentThread}
        onNewChat={handleNewConversation}
        onOpenThreads={openThreadsIndex}
        onFocusChange={handleFocusChange}
        threadCallbacks={threadCallbacks}
        activeMobilePane={isMobile ? activeMobilePane : undefined}
        onRestoreMobilePane={commitMobilePane}
        onLogout={handleLogout}
      />
      <SidebarInset id="main" tabIndex={-1} className={sidebarInsetClass} style={showBgInApp ? { ...insetStyle, backgroundColor: 'transparent' } : insetStyle}>
        {showBgInApp && (
          <ClientBackground className="absolute inset-0 z-0 w-full h-full min-w-full min-h-full object-cover pointer-events-none opacity-75 dark:opacity-70" />
        )}
        <div className={cn("relative flex flex-col flex-1 min-h-0", showBgInApp && "z-10")}>
          {showAppHeader && (
            <div
              className={mobileHeaderChromeOuterClass}
            >
              {isMobile ? (
                <div className={mobileHeaderChromeInnerClass}>{headerContent}</div>
              ) : (
                headerContent
              )}
            </div>
          )}
          {mainContent}
        </div>
      </SidebarInset>
    </ArtifactCanvasProvider>
  );

  const sidebarProviderContent = (
    <>
      {skipToMainLink}
      {sidebarShell}
    </>
  );

  return (
    <ThemeProvider key="auth" defaultTheme="system" storageKey={APP_THEME_STORAGE_KEY}>
      <AgentListeningProvider>
        <AgentPanelProvider value={agentPanelValue}>
          <SidebarProvider
            defaultOpen={sidebarDefaultOpen}
            disableKeyboardShortcut={disableSidebarKeyboardShortcut}
            className={sidebarProviderClassName}
          >
            {/* Keep the active chat/page together while navigation opens. */}
            <MobileNavFrame className={mobileSurfaceClass}>{sidebarProviderContent}</MobileNavFrame>
          </SidebarProvider>
        </AgentPanelProvider>
      </AgentListeningProvider>
      <UiViewportDebug />
    </ThemeProvider>
  );
}

function App() {
  return (
    <Router>
      <EmployerOrganisationProvider>
        <AppContent />
      </EmployerOrganisationProvider>
    </Router>
  );
}

export default App;
